import React, { useState, useContext } from "react";
import { Grid } from "@mui/material";
import { FaRegTimesCircle } from "react-icons/fa";
import axios from "axios";
import { api } from "constants/RotasAPI";
import CustomButton from "shared/components/button/CustomButton";
import CustomAlert from "shared/components/customAlert/CustomAlert";
import DocumentPreview from "shared/components/documentPreview/DocumentPreview";
import { Signature } from "shared/components/documentPreview/types/types";
import { GestaoAssinanteDocumentoDTO } from "types/dto/groupsign/GestaoAssinanteDocumentoDTO";
import { GestaoAssinaturaDTO } from "types/dto/groupsign/GestaoAssinaturaDTO";
import { LinhaTabela } from "shared/components/table/LinhaTabela";
import { RegistroTabela } from "shared/components/table/RegistroTabela";
import { LoadingContext } from "context/LoadingContext";
import { toast } from "react-hot-toast";
import { formatDateTimeBr } from "shared/util/formatters/dateTimeBr";
import { usePermissoesContext } from "context/permissoes/PermissoesContext";
import { TipoPermissao } from "constants/EnumTipoPermissao";

const STATUS = {
  ASSINADO: { label: "Assinado", backgroundColor: "#CAECCE", color: "#1E7D32" },
  RECUSADO: { label: "Assinatura recusada", backgroundColor: "#F2626F", color: "#FFFFFF" },
  PENDENTE: { label: "Assinatura pendente", backgroundColor: "#F1ECAE", color: "#A68A00" },
  CANCELADO: { label: "Assinatura cancelada", backgroundColor: "#DDD", color: "#3D3D3F" },
  EXPIRADO: { label: "Prazo expirado", backgroundColor: "#FDB8BD", color: "#D32F2F" },
};

const VISUALIZAR_ACTION_LABELS = {
  PENDENTE: "Ver assinaturas",
  EXPIRADO: "Ver documento",
  CANCELADO: "Ver documento",
  ASSINADO: "Ver assinaturas",
  RECUSADO: "Ver motivo da recusa",
};

const TIPO_DOCUMENTO_LABELS = {
  PASTA_PRESTACAO_CONTAS: "Pasta de prestação de contas",
};

interface RenderRowGestaoAssinaturaProps {
  data: GestaoAssinaturaDTO[];
  refetch: () => void;
}

const RenderRowGestaoAssinatura: React.FC<RenderRowGestaoAssinaturaProps> = ({ data, refetch }) => {
  const [showAlert, setShowAlert] = useState(false);
  const [selectedGestao, setSelectedGestao] = useState<GestaoAssinaturaDTO | null>(null);
  const [showPreviewDocument, setShowPreviewDocument] = useState(false);
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  const [signatures, setSignatures] = useState<Signature[]>([]);
  const [titleDocumentPreview, setTitleDocumentPreview] = useState<string>("");
  const { setLoading } = useContext(LoadingContext);
  const { usuarioPossuiPermissao } = usePermissoesContext();
    const possuiPermissaoCancelarAssinatura = usuarioPossuiPermissao(
        "cancelarAssinaturaDigital",
        TipoPermissao.EXECUTAR
    );

  const handleCancelarAssinatura = (gestao: GestaoAssinaturaDTO) => {
    if (!gestao.uuid) {
      return;
    }

    setSelectedGestao(gestao);
    setShowAlert(true);
  };

  const confirmarCancelamentoAssinatura = async () => {
    if (!selectedGestao?.id) {
      return;
    }

    try {
      setShowAlert(false);

      await axios.delete(
        `${api.groupsign.gestaoAssinaturaDigitalDocumento}/cancelar/${selectedGestao.id}`
      );

      toast.success("Cancelamento da assinatura realizado com sucesso.");

      setSelectedGestao(null);
      refetch();
    } catch (error) {
      toast.error("Erro ao cancelar a assinatura. Tente novamente.");
      setSelectedGestao(null);
    }
  };

  const closeAlert = () => {
    setShowAlert(false);
    setSelectedGestao(null);
  };

  const mapAssinanteToSignature = async (assinante: GestaoAssinanteDocumentoDTO): Promise<Signature | null> => {
    const statusKey = (assinante.status || "PENDENTE") as keyof typeof STATUS;
    const dataHora = assinante.dataHoraAcao || assinante.dataHoraEnvio || "";

    return {
      id: assinante.id.toString(),
      nome: STATUS[statusKey]?.label || assinante.nomeAssinante || "",
      email: assinante.emailAssinante || "",
      observacoes: assinante.status === "RECUSADO" || assinante.status === "ASSINADO" ? assinante.observacoes || "" : "",
      // ISO cru: a timeline formata uma única vez em America/Sao_Paulo
      data: dataHora,
      status: assinante.status || "",
    };
  };

  const handlePreviewDocument = async (gestao: GestaoAssinaturaDTO) => {
    try {
      setLoading(true);
      const gestaoAssinanteDocumentos = await Promise.all(gestao.assinantes.map(mapAssinanteToSignature));
      const filteredSignatures = gestaoAssinanteDocumentos.filter((signature) => signature !== null);

      const pdfUrl = await fetchDocumentUrl(gestao.uuid || "");
      setPdfUrl(pdfUrl);
      setSignatures(filteredSignatures as Signature[]);
      setShowPreviewDocument(true);
    } catch (error) {
      console.error("Erro ao processar o documento:", error);
      if (gestao.status === "CANCELADO") {
        toast.error("A visualização deste documento não está disponível, pois o processo de assinatura foi cancelado.");
      } else {
        toast.error("Erro ao carregar o documento. Tente novamente.");
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchDocumentUrl = async (uuid: string) => {
    try {
      const response = await axios.get(`${api.groupsign.getUrlByUuid}/${uuid}`);
      return response.data;
    } catch (error) {
      console.error("Erro ao buscar a URL do documento:", error);
      throw error;
    }
  };

  const renderStatusBadge = (status: string) => (
    <Grid display="flex" justifyContent="center" alignItems="center" width="100%">
      <span
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          color: STATUS[status as keyof typeof STATUS]?.color || "#DDD",
          backgroundColor: STATUS[status as keyof typeof STATUS]?.backgroundColor || "#DDD",
          borderRadius: "50px",
          width: "80%",
          maxWidth: "200px",
        }}
      >
        {STATUS[status as keyof typeof STATUS]?.label || status}
      </span>
    </Grid>
  );

  const renderCancelarButton = (gestao: GestaoAssinaturaDTO) => (
      <span
        className="link-configurar-ativo"
        style={{ fontSize: 12, fontWeight: "bold", marginLeft: 15, display: "flex", alignItems: "center" }}
        onClick={() => {
          handleCancelarAssinatura(gestao);
        }}
      >
        {"Cancelar"}
      </span>
  );

  const renderVisualizarButton = (gestao: GestaoAssinaturaDTO) => (
    <span
      className="link-configurar-ativo"
      style={{ fontSize: 12, fontWeight: "bold", marginLeft: 15, display: "flex", alignItems: "center" }}
      onClick={() => {
        if (gestao.status === "RECUSADO") {
          setTitleDocumentPreview("Ver motivo da recusa");
        } else if (gestao.status === "EXPIRADO" || gestao.status === "CANCELADO") {
          setTitleDocumentPreview("Ver documento");
        } else {
          setTitleDocumentPreview("Ver assinaturas");
        }
        handlePreviewDocument(gestao);
      }}
    >
      {VISUALIZAR_ACTION_LABELS[gestao.status as keyof typeof VISUALIZAR_ACTION_LABELS]}
    </span>
  );

  const renderRow = (gestao: GestaoAssinaturaDTO) => {
    const dataFormatada = gestao.dataHoraEnvio
      ? formatDateTimeBr(gestao.dataHoraEnvio, "dd/MM/yyyy 'às' HH:mm")
      : "Data não disponível";

    return (
      <LinhaTabela id={`id-gestao-assinatura-${gestao.uuid}`} key={gestao.uuid}>
        <RegistroTabela id={`id-condominio-${gestao.uuid}`}>
          <Grid container display="flex" alignItems="center">
            <Grid item xs={12} md={12} lg={12}>
              {gestao.condominioNome}
            </Grid>
            <Grid item xs={12} md={12} lg={12}>
              {`Documento enviado por: ${gestao.nomeUsuarioEnvio}, ${dataFormatada}`}
            </Grid>
          </Grid>
        </RegistroTabela>
        <RegistroTabela id={`id-documento-${gestao.uuid}`}>
          {TIPO_DOCUMENTO_LABELS[gestao.tipoDocumento as keyof typeof TIPO_DOCUMENTO_LABELS]}
        </RegistroTabela>
        <RegistroTabela id={`id-status-${gestao.uuid}`}>
          {renderStatusBadge(gestao.status || "PENDENTE")}
        </RegistroTabela>
        <RegistroTabela id={`btn-${gestao.uuid}`}>
          <Grid display="flex" alignItems="center">
            {renderVisualizarButton(gestao)}
            {gestao.status === "PENDENTE" && possuiPermissaoCancelarAssinatura && (
              renderCancelarButton(gestao)
            )}
          </Grid>
        </RegistroTabela>
      </LinhaTabela>
    );
  };

  return (
    <>
      {data.map(renderRow)}

      {showPreviewDocument && pdfUrl && (
        <DocumentPreview
          url={pdfUrl}
          open={showPreviewDocument}
          onClose={() => setShowPreviewDocument(false)}
          title={titleDocumentPreview}
          signatures={signatures}
        />
      )}

      {selectedGestao && (
        <CustomAlert
          icon={
            <span
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                width: 50,
                height: 50,
                backgroundColor: "#EFA2A2",
                borderRadius: 4,
              }}
            >
              <FaRegTimesCircle size={24} color="#C72D2E" />
            </span>
          }
          open={showAlert}
          onClose={closeAlert}
          title="Cancelar assinatura do documento"
          message={
            <>
              <p>
                Tem certeza que deseja cancelar a assinatura do documento{" "}
                <strong>
                  "{TIPO_DOCUMENTO_LABELS[selectedGestao.tipoDocumento as keyof typeof TIPO_DOCUMENTO_LABELS]}"
                </strong>
                ?
              </p>
              <ul>
                <li>As assinaturas feitas até o momento do cancelamento deixarão de ser válidas.</li>
                <li>Quem ainda não assinou não poderá mais acessar o documento para assinatura.</li>
              </ul>
              <p>Caso necessário, será preciso enviar um novo documento para assinatura.</p>
            </>
          }
          messageAlignment="left"
          height="35vw"
          width="40vw"
          buttons={
            <>
              <CustomButton
                id="voltar"
                label="Voltar"
                onClick={closeAlert}
                size="lg"
                className="me-3"
                style={{ width: 150 }}
                variant="outline-primary"
              />
              <CustomButton
                id="cancelar-assinatura"
                label="Cancelar assinatura"
                onClick={confirmarCancelamentoAssinatura}
                size="lg"
                style={{ width: 200 }}
                variant="primary"
              />
            </>
          }
        />
      )}
    </>
  );
};

export default RenderRowGestaoAssinatura;
